import React, { Component } from "react";
import { Modal } from "reactstrap";
import { toast } from "react-toastify";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { ConsolePage } from "../../components/Console/ConsoleLayout";
import {
  C, Panel, Chip, Btn, IconBtn, Field, Toggle, DialogBtn, ConfirmDialog,
} from "../../components/Console/ConsoleUI";
import {
  canWrite,
  isSuperAdmin,
  hasModuleAccess,
} from "../../services/PermissionService";
import {
  createFoodSettings,
  getFoodSettings,
  updateFoodSettings,
  deleteFoodSettings,
} from "../../Endpoint/Admin/index";

/**
 * Settings (F6 of the handoff).
 *
 * The design draws six configuration sections and cards for payment methods
 * and staff notifications. This route owns exactly one of them — the food
 * service windows — and there is no endpoint here for the others, so the rail
 * links to the settings screens that genuinely exist elsewhere in the console
 * rather than drawing dead sections, and the main pane shows the windows plus
 * the week they actually cover.
 */

const DAYS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

// Settings destinations that really exist. Each carries its own gate so this
// rail can never open a door the sidebar keeps shut.
const SECTIONS = [
  { key: "hours", icon: "schedule", label: "Food service hours", to: null },
  { key: "cuisine", icon: "restaurant_menu", label: "Cuisines", to: "/Foodcuisine", mod: "FOOD" },
  { key: "addon", icon: "add_circle", label: "Food add-ons", to: "/FoodAddOn", mod: "FOOD" },
  { key: "transport", icon: "local_taxi", label: "Transport", to: "/transport/settings", mod: "TRANSPORTATION" },
  { key: "loyalty", icon: "redeem", label: "Loyalty", to: "/loyalty/settings", mod: "LOYALTY" },
  { key: "sms", icon: "sms", label: "SMS templates", to: "/ManageNotification", mod: "NOTIFICATION" },
  { key: "branches", icon: "apartment", label: "Branches", to: "/branches", superOnly: true },
  { key: "app", icon: "phone_iphone", label: "App version", to: "/AppVersionSettings", superOnly: true },
];

const CSS = `
.fs-nav{display:flex;align-items:center;gap:12px;height:44px;padding:0 14px;border-radius:12px;
  color:${C.slate};font-size:15px;cursor:pointer;border:0;background:transparent;width:100%;
  text-align:left;font-family:inherit}
.fs-nav:hover{background:${C.surface}}
.fs-nav.is-active{background:${C.brown};color:#fff;cursor:default}
.fs-day{padding:8px 14px;border-radius:17px;font-size:13px;font-family:inherit;cursor:pointer;
  background:#fff;border:1px solid ${C.border};color:${C.taupe}}
.fs-day.is-on{background:${C.brown};border-color:${C.brown};color:#fff}
`;

const fmt = (d) =>
  d
    ? new Date(d).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: true })
    : "—";

class FoodSettings extends Component {
  constructor(props) {
    super(props);
    this.state = {
      items: [],
      isLoaded: false,
      modal: false,
      startTime: new Date(),
      endTime: new Date(),
      isActive: true,
      daysOfWeek: [],
      settingId: "",
      confirmDelete: false,
      deleteId: null,
      deleteLoading: false,
    };
    this.toggle = this.toggle.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleCloseModal = this.handleCloseModal.bind(this);
    this.editSetting = this.editSetting.bind(this);
    this.removeSetting = this.removeSetting.bind(this);
  }

  componentDidMount() {
    this.getAllSettings();
  }

  toggle() {
    this.setState((prevState) => ({
      modal: !prevState.modal,
      startTime: new Date(),
      endTime: new Date(),
      isActive: true,
      daysOfWeek: [],
      settingId: "",
    }));
  }

  toggleDay = (day) => {
    const { daysOfWeek } = this.state;
    this.setState({
      daysOfWeek: daysOfWeek.includes(day)
        ? daysOfWeek.filter((d) => d !== day)
        : [...daysOfWeek, day],
    });
  };

  handleCloseModal() {
    this.setState({
      modal: false,
    });
  }

  handleSubmit(e) {
    if (e && e.preventDefault) e.preventDefault();
    const { startTime, endTime, isActive, daysOfWeek, settingId } = this.state;

    // Form validation
    if (startTime >= endTime) {
      toast.error("End time must be after start time");
      return;
    }
    if (!daysOfWeek.length) {
      toast.error("Pick at least one day");
      return;
    }

    const data = {
      startTime: startTime.toISOString(),
      endTime: endTime.toISOString(),
      isActive,
      daysOfWeek,
    };

    if (!settingId) {
      createFoodSettings(data)
        .then((response) => {
          if (response && response.success) {
            this.getAllSettings();
            this.handleCloseModal();
            toast.success(response.message);
          } else {
            toast.error(
              response ? response.message : "Error creating food settings"
            );
          }
        })
        .catch((error) => {
          toast.error("Error creating food settings");
        });
    } else {
      updateFoodSettings({ id: settingId, ...data })
        .then((response) => {
          if (response && response.success) {
            this.getAllSettings();
            this.handleCloseModal();
            toast.success(response.message);
          } else {
            toast.error(
              response ? response.message : "Error updating food settings"
            );
          }
        })
        .catch((error) => {
          toast.error("Error updating food settings");
        });
    }
  }

  getAllSettings() {
    getFoodSettings()
      .then((result) => {
        if (result && result.success) {
          this.setState({
            isLoaded: true,
            items: result.data || [],
          });
        } else {
          toast.error("Error fetching settings");
        }
      })
      .catch((error) => {
        toast.error("Error fetching settings");
      });
  }

  editSetting(setting) {
    this.setState({
      startTime: new Date(setting.startTime),
      endTime: new Date(setting.endTime),
      isActive: !!setting.isActive,
      daysOfWeek: setting.daysOfWeek || [],
      settingId: setting._id,
      modal: true,
    });
  }

  removeSetting(id) {
    this.setState({ deleteLoading: true });
    deleteFoodSettings(id)
      .then((response) => {
        if (response && response.success) {
          this.getAllSettings();
          toast.success(response.message);
        } else {
          toast.error(response ? response.message : "Error deleting settings");
        }
      })
      .catch((error) => {
        toast.error("Error deleting settings");
      })
      .finally(() => {
        this.setState({ deleteLoading: false, confirmDelete: false, deleteId: null });
      });
  }

  // --- Rail -----------------------------------------------------------------

  visibleSections() {
    return SECTIONS.filter((s) => {
      if (!s.to) return true;
      if (s.superOnly) return isSuperAdmin();
      return s.mod ? hasModuleAccess(s.mod) : true;
    });
  }

  renderRail() {
    const { items } = this.state;
    const active = items.filter((i) => i.isActive).length;

    return (
      <div
        style={{
          background: "#fff", border: `1px solid ${C.tint}`, borderRadius: 18,
          padding: 16, display: "flex", flexDirection: "column", gap: 4,
        }}
      >
        {this.visibleSections().map((s) => (
          <button
            key={s.key}
            type="button"
            className={`fs-nav ${s.to ? "" : "is-active"}`}
            onClick={() => s.to && this.props.history.push(s.to)}
          >
            <span className="ms" style={{ fontSize: 20 }}>{s.icon}</span>
            {s.label}
          </button>
        ))}

        <div
          style={{
            marginTop: 18, padding: 14, borderRadius: 12, background: C.iconTint,
          }}
        >
          <div style={{ fontSize: 13, fontWeight: 500, color: C.brownDeep }}>
            Food service
          </div>
          <div style={{ marginTop: 3, fontSize: 13, color: C.taupe }}>
            {items.length} window{items.length === 1 ? "" : "s"} · {active} active
          </div>
        </div>
      </div>
    );
  }

  // --- Cards ----------------------------------------------------------------

  renderWindows() {
    const { items, isLoaded } = this.state;
    const write = canWrite();

    return (
      <Panel
        title="Service windows"
        subtitle="When the kitchen accepts orders from the app"
        actions={
          write ? (
            <Btn kind="quiet" icon="add" onClick={this.toggle} style={{ height: 36 }}>
              Add
            </Btn>
          ) : null
        }
      >
        <div style={{ marginTop: 16 }}>
          {!isLoaded ? (
            <div style={{ padding: "30px 0", color: C.taupeLight, fontSize: 14 }}>
              Loading windows…
            </div>
          ) : items.length === 0 ? (
            <div style={{ padding: "30px 0", color: C.taupeLight, fontSize: 14 }}>
              No service window set — the kitchen has no published hours.
            </div>
          ) : (
            items.map((it, i) => (
              <div
                key={it._id}
                style={{
                  display: "flex", alignItems: "center", gap: 14, padding: "14px 0",
                  borderBottom: i === items.length - 1 ? "none" : `1px solid ${C.panel}`,
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 15, color: C.ink }}>
                    {fmt(it.startTime)} — {fmt(it.endTime)}
                  </div>
                  <div style={{ marginTop: 2, fontSize: 13, color: C.taupeLight }}>
                    {(it.daysOfWeek || []).length === 7
                      ? "Every day"
                      : (it.daysOfWeek || []).map((d) => d.slice(0, 3)).join(" · ") || "No days set"}
                  </div>
                </div>
                <Chip tone={it.isActive ? "good" : "neutral"}>
                  {it.isActive ? "Active" : "Paused"}
                </Chip>
                {write && (
                  <React.Fragment>
                    <IconBtn icon="edit" title="Edit window" onClick={() => this.editSetting(it)} />
                    <IconBtn
                      icon="delete"
                      title="Delete window"
                      tone="bad"
                      onClick={() => this.setState({ confirmDelete: true, deleteId: it._id })}
                    />
                  </React.Fragment>
                )}
              </div>
            ))
          )}
        </div>
      </Panel>
    );
  }

  renderWeek() {
    const { items, isLoaded } = this.state;

    return (
      <Panel
        title="The week as guests see it"
        subtitle="Only active windows count"
      >
        <div style={{ marginTop: 16, display: "flex", flexDirection: "column" }}>
          {DAYS.map((day, i) => {
            const windows = items.filter(
              (it) => it.isActive && (it.daysOfWeek || []).includes(day)
            );
            return (
              <div
                key={day}
                style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  gap: 12, padding: "13px 0",
                  borderBottom: i === DAYS.length - 1 ? "none" : `1px solid ${C.panel}`,
                }}
              >
                <span style={{ fontSize: 15, color: C.ink }}>{day}</span>
                <span
                  style={{
                    fontSize: 14, textAlign: "right",
                    color: windows.length ? C.slate : C.taupeLight,
                  }}
                >
                  {!isLoaded
                    ? "…"
                    : windows.length
                    ? windows.map((w) => `${fmt(w.startTime)} — ${fmt(w.endTime)}`).join(", ")
                    : "Closed"}
                </span>
              </div>
            );
          })}
        </div>
      </Panel>
    );
  }

  renderModal() {
    const { modal, startTime, endTime, isActive, daysOfWeek, settingId } = this.state;

    return (
      <Modal isOpen={modal} toggle={this.toggle} className="con-modal" centered>
        <div style={{ padding: 30 }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 21, fontWeight: 500, color: C.ink }}>
                {settingId ? "Edit service window" : "New service window"}
              </div>
              <div style={{ marginTop: 4, fontSize: 14, color: C.taupe }}>
                Guests can only order food inside an active window.
              </div>
            </div>
            <button
              type="button"
              onClick={this.toggle}
              aria-label="Close"
              style={{ border: 0, background: "transparent", color: C.taupeLight, cursor: "pointer", padding: 4, lineHeight: 1 }}
            >
              <span className="ms" style={{ fontSize: 22 }}>close</span>
            </button>
          </div>

          <div style={{ marginTop: 22, display: "flex", flexDirection: "column", gap: 18 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
              <Field label="Opens">
                <DatePicker
                  selected={startTime}
                  onChange={(date) => this.setState({ startTime: date })}
                  showTimeSelect
                  showTimeSelectOnly
                  timeIntervals={15}
                  timeCaption="Time"
                  dateFormat="h:mm aa"
                  className="form-control"
                />
              </Field>
              <Field label="Closes">
                <DatePicker
                  selected={endTime}
                  onChange={(date) => this.setState({ endTime: date })}
                  showTimeSelect
                  showTimeSelectOnly
                  timeIntervals={15}
                  timeCaption="Time"
                  dateFormat="h:mm aa"
                  className="form-control"
                />
              </Field>
            </div>

            <Field
              label="Days"
              hint={daysOfWeek.length ? null : "Pick at least one day"}
            >
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {DAYS.map((d) => (
                  <button
                    key={d}
                    type="button"
                    className={`fs-day ${daysOfWeek.includes(d) ? "is-on" : ""}`}
                    onClick={() => this.toggleDay(d)}
                  >
                    {d.slice(0, 3)}
                  </button>
                ))}
              </div>
            </Field>

            <div
              style={{
                display: "flex", alignItems: "center", gap: 14,
                padding: "14px 16px", borderRadius: 14, background: C.surface,
              }}
            >
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 15, color: C.ink }}>Active</div>
                <div style={{ marginTop: 2, fontSize: 13, color: C.taupeLight }}>
                  Paused windows stay saved but do not open the kitchen.
                </div>
              </div>
              <Toggle
                on={isActive}
                title="Active"
                onClick={() => this.setState({ isActive: !isActive })}
              />
            </div>
          </div>

          <div style={{ marginTop: 26, display: "flex", gap: 12 }}>
            <DialogBtn onClick={this.toggle}>Cancel</DialogBtn>
            <DialogBtn kind="primary" onClick={this.handleSubmit}>
              {settingId ? "Save window" : "Create window"}
            </DialogBtn>
          </div>
        </div>
      </Modal>
    );
  }

  render() {
    const s = this.state;
    const write = canWrite();

    return (
      <ConsolePage
        title="Settings"
        actions={
          write ? (
            <Btn icon="add" onClick={this.toggle}>
              Add service window
            </Btn>
          ) : null
        }
      >
        <style dangerouslySetInnerHTML={{ __html: CSS }} />

        <div
          style={{
            display: "grid", gridTemplateColumns: "232px minmax(0,1fr)",
            gap: 20, alignItems: "start",
          }}
        >
          {this.renderRail()}

          <div
            style={{
              display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))",
              gap: 20, alignItems: "start",
            }}
          >
            {this.renderWindows()}
            {this.renderWeek()}
          </div>
        </div>

        {this.renderModal()}

        <ConfirmDialog
          isOpen={s.confirmDelete}
          toggle={() => this.setState({ confirmDelete: false, deleteId: null })}
          onConfirm={() => this.removeSetting(s.deleteId)}
          icon="delete"
          title="Delete this service window?"
          body="Guests will no longer be able to order food during these hours."
          tip="If you only want to stop taking orders for now, edit the window and switch it to paused instead — that is reversible."
          confirmText="Delete window"
          loading={s.deleteLoading}
        />
      </ConsolePage>
    );
  }
}

export default FoodSettings;
